import React, { useEffect, useState } from 'react';

import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';

import TableTree, { Cell, Header, Headers, Row, Rows } from '@atlaskit/table-tree';

import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';

import { IconButton } from '@atlaskit/button/new';
import Popup from '@atlaskit/popup';
import Lozenge, { type ThemeAppearance } from '@atlaskit/lozenge';
import { ButtonItem, LinkItem, Section } from '@atlaskit/menu';
import Spinner from '@atlaskit/spinner';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import type { ApplicationLinkV3Data, ApplicationLinkV3Status } from '../../../common/entities.tsx';
import { deleteApplink, fetchApplinkStatus } from '../../../rest/index.tsx';
import {
	APPLINKS_EDIT_PAGE_URL_FOR_ID,
	APPLINKS_LIST_PAGE_URL,
} from '../../../common/constants.tsx';
import { lookupTypeFromV3Data } from '../../../common/application-types.tsx';

const contentStyles = cssMap({
	status: {
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
		maxWidth: '500px',
		minWidth: '320px',
	},
	menu: {
		maxWidth: '500px',
		minWidth: '320px',
	},
});

const ApplicationLinkProduct = (props: ApplicationLinkV3Data) => {
	const typeDef = lookupTypeFromV3Data(props);

	return (
		<>
			<Inline alignBlock="center" space="space.100">
				{typeDef.icon}
				<Stack>
					<Text>{props.displayUrl}</Text>
					<Text color="color.text.subtle" size="small">
						{typeDef.name}
					</Text>
				</Stack>
			</Inline>
		</>
	);
};

const ApplicationLinkName = (props: ApplicationLinkV3Data) => {
	return <Text>{props.name}</Text>;
};

const ApplicationLinkType = (props: ApplicationLinkV3Data) => {
	const appendLozenges = (isPrimary: boolean, isSystem: boolean, isExtraAuthRequired: boolean) => {
		return (
			<>
				{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
				{isPrimary && <Lozenge key="primary">Primary</Lozenge>}
				{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
				{isExtraAuthRequired && <Lozenge key="extra-auth">Extra Auth</Lozenge>}
			</>
		);
	};

	return (
		<Inline alignBlock="center" space="space.100">
			{appendLozenges(props.primary, props.system, props.isExtraAuthRequired)}
		</Inline>
	);
};

const LozengeWithContext = (props: {
	appearance: ThemeAppearance;
	label: string;
	content: React.ReactNode;
}) => {
	const [isOpen, setIsOpen] = useState(false);

	return (
		<Inline alignBlock="center">
			<Lozenge appearance={props.appearance}>{props.label}</Lozenge>
			<Popup
				shouldRenderToParent
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				placement="bottom-start"
				content={() => <Box xcss={contentStyles.status}>{props.content}</Box>}
				trigger={(triggerProps) => (
					<IconButton
						{...triggerProps}
						icon={StatusInformationIcon}
						label={`Additional information about ${props.label} status`}
						appearance="subtle"
						onClick={() => setIsOpen(!isOpen)}
					/>
				)}
			/>
		</Inline>
	);
};

const ApplicationLinkStatus = (props: ApplicationLinkV3Data) => {
	const [applinkStatus, setApplinkStatus] = useState<ApplicationLinkV3Status | 'system' | null>(
		null,
	);

	useEffect(() => {
		// If the applink is a system link, we don't need to fetch its status
		if (props.system) {
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
			setApplinkStatus('system');
			return;
		}

		// TODO: Properly mock data
		fetchApplinkStatus(props.id)
			.then((status: ApplicationLinkV3Status) => {
				setApplinkStatus(status);
			})
			.catch((err) => {
				const maybeError = err instanceof Error ? err : undefined;
				log.safeErrorWithoutCustomerData(
					'applinks.administration.list.fetchApplinkStatus',
					`Failed to fetch applink status for the table row belonging to applink ${props.id}`,
					maybeError,
				);
			});
	}, [props.id, props.system]);

	// TODO: Pop-ups
	const createLozenge = (contents: ApplicationLinkV3Status | 'system') => {
		if (contents === 'system') {
			return (
				<LozengeWithContext
					appearance="default"
					label="System"
					content={
						<Stack space="space.100">
							{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
							<Heading size="small">Internal System Link</Heading>
							{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
							<Text>
								This is an internal link to a Cloud server, created automatically by the system.
								System internal links are read-only and cannot be edited.
							</Text>
						</Stack>
					}
				/>
			);
		}

		// First, check if the link is working
		if (contents.working) {
			// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
			return <Lozenge appearance="success">Connected</Lozenge>;
		}

		// Next, check if there is an error
		if (contents.error) {
			return (
				<LozengeWithContext
					appearance="removed"
					label="Config Error"
					content={
						<Stack space="space.100">
							{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
							<Heading size="small">Config Error</Heading>
							<Text>{JSON.stringify(contents.error)}</Text>
						</Stack>
					}
				/>
			);
		}

		// TODO: Others
		// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
		return <Lozenge>Unknown</Lozenge>;
	};

	return applinkStatus ? createLozenge(applinkStatus) : <Spinner />;
};

const onDeleteButtonClick = (applink: ApplicationLinkV3Data) => {
	deleteApplink(applink.id);
};

const ApplicationLinkPopupMenuContent = (props: { data: ApplicationLinkV3Data }) => {
	return (
		<Box>
			<Stack xcss={contentStyles.menu}>
				<Section>
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					<LinkItem href={props.data.displayUrl + APPLINKS_LIST_PAGE_URL}>Go to remote</LinkItem>
				</Section>
				<Section title="Manage link" hasSeparator>
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					<LinkItem href={APPLINKS_EDIT_PAGE_URL_FOR_ID(props.data.id)}>Edit</LinkItem>
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					<ButtonItem onClick={() => onDeleteButtonClick(props.data)}>Delete</ButtonItem>
				</Section>
			</Stack>
		</Box>
	);
};

const ApplicationLinkPopupMenu = (props: { data: ApplicationLinkV3Data }) => {
	const [isOpen, setIsOpen] = useState(false);

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			content={() => <ApplicationLinkPopupMenuContent data={props.data} />}
			placement="bottom-start"
			shouldRenderToParent
			trigger={(triggerProps) => (
				<IconButton
					{...triggerProps}
					isSelected={isOpen}
					label="Additional actions"
					onClick={() => {
						setIsOpen(!isOpen);
					}}
					icon={ShowMoreHorizontalIcon}
				/>
			)}
		/>
	);
};

type Item = {
	id: string;
	content: ApplicationLinkV3Data;
	hasChildren: boolean;
	children?: Item[];
};

const convertToArray = (applinks: ApplicationLinkV3Data[]): Item[] => {
	const wrapWithItem = (
		id: string,
		content: ApplicationLinkV3Data,
		hasChildren: boolean,
		children?: Item[],
	) => {
		return { id, content, hasChildren, children };
	};

	return applinks.map((entry, index) => {
		return wrapWithItem(`entry-${index}`, entry, false);
	});
};

const ApplinksTable = (props: {
	search: string;
	filter: string | null;
	applinks: ApplicationLinkV3Data[] | null;
}): React.JSX.Element => {
	const applinkItems = props.applinks ? convertToArray(props.applinks) : undefined;

	const filteredApplinkItems = applinkItems?.filter((item) => {
		let passesFilter = true;
		if (props.filter) {
			passesFilter = item.content.type === props.filter;
		}

		let passesSearch = true;
		if (props.search && props.search.length > 0) {
			const nameCondition = item.content.name.toLowerCase().includes(props.search.toLowerCase());
			const urlCondition = item.content.displayUrl
				.toLowerCase()
				.includes(props.search.toLowerCase());
			passesSearch = nameCondition || urlCondition;
		}
		return passesFilter && passesSearch;
	});

	return (
		<TableTree label="Application Links">
			<Headers>
				{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
				<Header width="20%">Name</Header>
				{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
				<Header width="40%">Application</Header>
				{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
				<Header width="15%">Link properties</Header>
				{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
				<Header width="15%">Status</Header>
				{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
				<Header width="10%">Actions</Header>
			</Headers>
			<Rows
				items={filteredApplinkItems}
				render={(item: Item) => {
					const { content } = item;

					const row = (
						<Row key={item.id} itemId={item.id} hasChildren={item.hasChildren}>
							<Cell>
								<ApplicationLinkName {...content} />
							</Cell>
							<Cell>
								<ApplicationLinkProduct {...content} />
							</Cell>
							<Cell>
								<ApplicationLinkType {...content} />
							</Cell>
							<Cell>
								<ApplicationLinkStatus {...content} />
							</Cell>
							<Cell>
								<ApplicationLinkPopupMenu data={item.content} />
							</Cell>
						</Row>
					);

					return row;
				}}
			/>
		</TableTree>
	);
};

export default ApplinksTable;
